/* Add to cart from the wall without leaving the page, and without opening the drawer.
 *
 * Omar, 2026-09-05, in two steps: first "I just want it added to cart and stay on the same page",
 * then "do not open the cart drawer, give a little added confirm message on the button".
 *
 * WHY IT NAVIGATED ORIGINALLY. The tile renders a plain <form action="/cart/add" method="post">.
 * With no JS on it that is a full page POST, and Shopify answers by redirecting to /cart. Nothing
 * was broken; it was simply never wired up.
 *
 * ⚠️ IT DOES NOT TOUCH THE MARKUP OR THE GATE. The form is still a real form with a real action,
 * so if this script fails to load, or JS is off, the button still works and still adds the item,
 * it just navigates like before. The dealer guard is upstream in Liquid: for a logged out visitor
 * there is no form in the HTML at all, so there is nothing here to protect.
 *
 * WHY THE DRAWER STAYS SHUT. The wall is a browsing surface with 128 tiles on it. A drawer sliding
 * over the page on every add interrupts exactly the behaviour the wall is there to encourage, and
 * a dealer adding six lighters would have to dismiss it six times. The badge still updates, so the
 * cart is never out of date; it just does not demand attention.
 *
 * ⚠️ ERRORS MATTER ON THIS STORE. Every Zippo variant is inventory_policy DENY, and measured on
 * 2026-09-05 only 116 of 1,710 hold any stock, 90 of those holding 1-5 units. So "add one more
 * than exists" is a routine outcome, not an edge case, and Shopify answers with a 422 and a
 * readable reason. That reason is surfaced rather than swallowed.
 */
(function () {
  "use strict";

  var BUSY = "is-busy";
  var DONE = "is-added";
  var FAIL = "is-failed";
  var HOLD = 2000;

  // One polite live region for the whole page. Rewriting a button's own label does not get
  // announced, so a dealer on a screen reader would get silence on a successful add.
  var live;
  function announce(message) {
    if (!live) {
      live = document.createElement("p");
      live.className = "visually-hidden";
      live.setAttribute("aria-live", "polite");
      live.setAttribute("role", "status");
      document.body.appendChild(live);
    }
    live.textContent = message;
  }

  function refreshCartCount() {
    return fetch("/?sections=cart-drawer,cart-icon-bubble", { headers: { Accept: "application/json" } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        if (!data) return;
        var parser = new DOMParser();
        Object.keys(data).forEach(function (key) {
          var incoming = parser.parseFromString(data[key], "text/html");
          // ⚠️ FALL BACK TO body. The Sections API returns the section's rendered output, and
          // whether that output still carries the wrapper element with the id depends on the
          // section. cart-icon-bubble comes back WITHOUT it, so a plain getElementById lookup
          // found nothing and the badge silently kept its empty-cart icon after a successful add.
          // Verified 2026-09-05: item_count went to 1 while the bubble still showed icon-cart-empty.
          if (key === "cart-icon-bubble") {
            var bubble = document.getElementById("cart-icon-bubble");
            var next = incoming.getElementById("cart-icon-bubble") || incoming.body;
            if (bubble && next) bubble.innerHTML = next.innerHTML;
          } else {
            // The drawer is refreshed but NOT opened, so it is correct whenever the dealer
            // chooses to open it themselves.
            var drawer = document.querySelector("cart-drawer");
            var nextDrawer = incoming.querySelector("cart-drawer") || incoming.body;
            if (drawer && nextDrawer) {
              drawer.innerHTML = nextDrawer.innerHTML;
              drawer.classList.remove("is-empty");
            }
          }
        });
      })
      .catch(function () { /* The item is in the cart already; a stale badge is not worth throwing over. */ });
  }

  function confirmOn(button, label, stateClass) {
    // Stash the original label once. Re-reading it on a second click would capture "Added".
    if (!button.hasAttribute("data-bhx-label")) {
      button.setAttribute("data-bhx-label", button.innerHTML);
    }
    if (button.bhxTimer) clearTimeout(button.bhxTimer);
    button.classList.remove(DONE, FAIL);
    button.classList.add(stateClass);
    button.innerHTML = label;
    button.bhxTimer = setTimeout(function () {
      button.innerHTML = button.getAttribute("data-bhx-label");
      button.classList.remove(DONE, FAIL);
    }, HOLD);
  }

  document.addEventListener("submit", function (e) {
    var form = e.target;
    if (!form || form.tagName !== "FORM") return;
    // .bhx-gift__add: the free-gift promo buttons in the cart (bhx-gift-promo.liquid). Same
    // add-without-navigating behaviour as the wall tiles, reusing this listener rather than a
    // third copy of the fetch/confirm/announce dance.
    var button = form.querySelector(".bhx-wall__add") || form.querySelector(".bhx-gift__add");
    if (!button) return; // Not a wall tile or a gift button. Every other form is left alone.

    e.preventDefault();
    if (button.classList.contains(BUSY)) return;
    button.classList.add(BUSY);

    fetch("/cart/add.js", {
      method: "POST",
      headers: { Accept: "application/json" },
      body: new FormData(form),
    })
      .then(function (res) {
        return res.json().then(function (json) { return { ok: res.ok, json: json }; });
      })
      .then(function (result) {
        if (!result.ok) {
          var why = (result.json && result.json.description) || "Could not add";
          confirmOn(button, "Sold out", FAIL);
          announce(why);
          return;
        }
        // The dedicated /cart page renders its own line-item table and footer outside the
        // sections this listener otherwise refreshes (cart-drawer, cart-icon-bubble), so a
        // gift added from THAT page needs the whole page back, or the new line and the
        // "choose one" banner both sit stale until the dealer manually refreshes. The wall
        // tiles never hit this branch; they are not rendered on /cart.
        if (button.classList.contains("bhx-gift__add") && window.location.pathname === "/cart") {
          window.location.reload();
          return;
        }
        confirmOn(button, "Added ✓", DONE);
        announce("Added to cart");
        return refreshCartCount();
      })
      .catch(function () {
        confirmOn(button, "Failed", FAIL);
        announce("Could not add to cart");
      })
      .finally(function () { button.classList.remove(BUSY); });
  });
})();
